import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import {
  addRecipient,
  createShare,
  formatDuration,
  getShare,
  removeRecipient,
  revokeShare,
  type ShareAnalytics,
  type ShareRecord,
} from '../api/shares'
import { colors, radii, shadow, type } from '../theme'

/**
 * Share + analytics for one proposal.
 *
 * Two link flavours, and the difference matters to a rep:
 *   • the plain share link — one URL, safe to paste anywhere, tells you only
 *     that *someone* opened it.
 *   • a per-recipient link — one per person, so the timeline can name who
 *     opened it and when. Forwarding shows up as extra views on that
 *     recipient's link, which is itself a useful signal.
 */
export default function SharePanel({
  pid,
  hasPdf,
}: {
  pid: string
  hasPdf: boolean
}) {
  const { authHeaders, apiBase } = useAuth()
  const [share, setShare] = useState<ShareRecord | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [analytics, setAnalytics] = useState<ShareAnalytics | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  const load = useCallback(async () => {
    try {
      const res = await getShare(apiBase, authHeaders(), pid)
      setShare(res.share)
      setUrl(res.url)
      setAnalytics(res.analytics)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load share')
    }
    // authHeaders is recreated each render; pid + apiBase are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, pid])

  useEffect(() => {
    void load()
  }, [load])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  async function copy(value: string, key: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(key)
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800)
    } catch {
      setError('Clipboard blocked — select the link and copy it manually.')
    }
  }

  const totals = analytics?.totals

  return (
    <section
      style={{
        background: colors.panel,
        borderRadius: radii.lg,
        boxShadow: shadow.card,
        padding: 24,
        marginTop: 24,
      }}
    >
      <div style={{ ...labelStyle, marginBottom: 12 }}>Share &amp; track</div>

      {error && (
        <div
          style={{
            background: colors.warningBg,
            border: `1px solid ${colors.warningBorder}`,
            borderRadius: radii.md,
            padding: '10px 12px',
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {!share && (
        <div>
          <p style={{ fontSize: 14, color: colors.textMuted, marginTop: 0 }}>
            Publish this proposal to a client-facing link on{' '}
            <strong>proposals.parkify.com</strong>. The link is unguessable, works
            without a login, and records every open.
          </p>
          <button
            disabled={!hasPdf || busy}
            onClick={() => run(() => createShare(apiBase, authHeaders(), pid))}
            style={primaryBtn(!hasPdf || busy)}
            title={hasPdf ? undefined : 'Save or upload a PDF before sharing'}
          >
            {busy ? 'Creating…' : 'Create share link'}
          </button>
          {!hasPdf && (
            <div style={{ fontSize: 12, color: colors.textDim, marginTop: 8 }}>
              This proposal has no rendered PDF yet.
            </div>
          )}
        </div>
      )}

      {share && url && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: 18,
            }}
          >
            <code
              style={{
                flex: '1 1 320px',
                background: colors.purpleSoft,
                border: `1px solid ${colors.divider}`,
                borderRadius: radii.md,
                padding: '10px 12px',
                fontSize: 13,
                overflowWrap: 'anywhere',
              }}
            >
              {url}
            </code>
            <button onClick={() => copy(url, 'main')} style={primaryBtn(false)}>
              {copied === 'main' ? 'Copied' : 'Copy link'}
            </button>
            <a href={url} target="_blank" rel="noreferrer" style={ghostBtn}>
              Open
            </a>
            <button
              disabled={busy}
              onClick={() => {
                if (
                  !window.confirm(
                    'Revoke this link? Anyone who already has it — including the ' +
                      'client — will immediately get a "not available" page. This ' +
                      'cannot be undone; a new share gets a new URL.',
                  )
                )
                  return
                void run(() => revokeShare(apiBase, authHeaders(), pid))
              }}
              style={dangerBtn(busy)}
            >
              Revoke
            </button>
          </div>

          {totals && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 12,
                marginBottom: 22,
              }}
            >
              <Stat label="Views" value={String(totals.views)} />
              <Stat label="Unique viewers" value={String(totals.unique_viewers)} />
              <Stat label="Time on proposal" value={formatDuration(totals.seconds_viewed)} />
              <Stat label="Downloads" value={String(totals.downloads)} />
              <Stat
                label="Last opened"
                value={totals.last_viewed_at ? shortDate(totals.last_viewed_at) : '—'}
              />
            </div>
          )}

          {/* Per-recipient links */}
          <div style={{ ...labelStyle, marginBottom: 10 }}>Recipients</div>
          <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 0 }}>
            Give each person their own link and the timeline below can tell you
            who opened it — not just that someone did.
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              style={input}
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (optional)"
              style={input}
            />
            <button
              disabled={busy || !(name.trim() || email.trim())}
              onClick={() =>
                run(async () => {
                  await addRecipient(apiBase, authHeaders(), pid, {
                    name: name.trim() || undefined,
                    email: email.trim() || undefined,
                  })
                  setName('')
                  setEmail('')
                })
              }
              style={primaryBtn(busy || !(name.trim() || email.trim()))}
            >
              Add
            </button>
          </div>

          {analytics && analytics.recipients.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: colors.textDim }}>
                  <th style={th}>Who</th>
                  <th style={th}>Opened</th>
                  <th style={th}>Views</th>
                  <th style={th}>Time</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {analytics.recipients.map((r) => (
                  <tr key={r.token} style={{ borderTop: `1px solid ${colors.divider}` }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name || r.email || r.token}</div>
                      {r.name && r.email && (
                        <div style={{ color: colors.textDim, fontSize: 12 }}>{r.email}</div>
                      )}
                    </td>
                    <td style={td}>
                      {r.first_viewed_at ? shortDate(r.first_viewed_at) : 'Not yet'}
                    </td>
                    <td style={td}>{r.view_count}</td>
                    <td style={td}>{formatDuration(r.seconds_viewed)}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.url && (
                        <button onClick={() => copy(r.url as string, r.token)} style={ghostBtn}>
                          {copied === r.token ? 'Copied' : 'Copy link'}
                        </button>
                      )}{' '}
                      <button
                        disabled={busy}
                        onClick={() =>
                          run(() => removeRecipient(apiBase, authHeaders(), pid, r.token))
                        }
                        style={ghostBtn}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {analytics && analytics.timeline.length > 0 && (
            <>
              <div style={{ ...labelStyle, margin: '24px 0 10px' }}>Activity</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
                {analytics.timeline.slice(0, 25).map((t, i) => (
                  <li
                    key={`${t.at}-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '8px 0',
                      borderBottom: `1px solid ${colors.divider}`,
                    }}
                  >
                    <span>
                      <strong>{t.recipient_name || 'Someone'}</strong>{' '}
                      {t.type === 'download' ? 'downloaded the PDF' : 'opened the proposal'}
                      {t.country ? ` · ${t.country}` : ''}
                    </span>
                    <span style={{ color: colors.textDim, whiteSpace: 'nowrap' }}>
                      {shortDate(t.at)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {analytics && totals && totals.views === 0 && (
            <p style={{ fontSize: 13, color: colors.textDim }}>
              No opens yet. Views appear here within a few seconds of the client
              loading the link.
            </p>
          )}
        </>
      )}
    </section>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: colors.bgAlt,
        borderRadius: radii.md,
        padding: '12px 14px',
      }}
    >
      <div style={{ fontSize: 20, fontWeight: 700, color: colors.purple }}>{value}</div>
      <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2 }}>{label}</div>
    </div>
  )
}

function shortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const labelStyle = {
  fontSize: type.sectionLabel.size,
  fontWeight: type.sectionLabel.weight,
  letterSpacing: type.sectionLabel.tracking,
  textTransform: 'uppercase' as const,
  color: colors.textDim,
}

const input: React.CSSProperties = {
  flex: '1 1 160px',
  padding: '9px 11px',
  border: `1px solid ${colors.divider}`,
  borderRadius: radii.md,
  fontSize: 13,
}

function primaryBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? colors.gray : colors.purple,
    color: colors.white,
    border: 'none',
    borderRadius: radii.md,
    padding: '10px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }
}

function dangerBtn(disabled: boolean): React.CSSProperties {
  return {
    background: 'transparent',
    color: colors.red,
    border: `1px solid ${colors.warningBorder}`,
    borderRadius: radii.md,
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }
}

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  color: colors.purple,
  border: `1px solid ${colors.divider}`,
  borderRadius: radii.md,
  padding: '8px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
}

const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600 }
const td: React.CSSProperties = { padding: '10px 8px', verticalAlign: 'top' }
